/** A published model, or a saved draft — both are `Model` rows and live at `/editor/:modelId`. */
export type ModelTabTarget = {
  kind: "model";
  modelId: string;
  /**
   * Set when the model is the query behind a dashboard tile. Context, not identity: see `tabKey`.
   */
  dashboardId?: string;
  tileId?: string;
};

/** An unsaved draft, held in localStorage rather than the API. */
export type DraftTabTarget = { kind: "draft"; draftId: string };

export type DashboardTabTarget = { kind: "dashboard"; dashboardId: string };

/** A tab with nothing open in it yet. */
export type ScratchTabTarget = { kind: "scratch" };

export type TabTarget =
  ModelTabTarget | DraftTabTarget | DashboardTabTarget | ScratchTabTarget;

export type Tab = {
  /** Stable identity for React keys and reordering. Not the same as `tabKey`. */
  id: string;
  target: TabTarget;
  /**
   * Last label this tab rendered. Persisted so a reload has something to show before the entity
   * resolves, and so a tab whose entity is gone can still name what it lost.
   *
   * For a scratch tab there is nothing to resolve, so this is the label outright — see
   * `nextScratchLabel`.
   */
  cachedLabel?: string;
};

/**
 * Identity for answering "is this already open?".
 *
 * A model's dashboard context is excluded on purpose: opening a model that is embedded in a
 * dashboard tile re-points the tab that model already has instead of opening a second one.
 *
 * Scratch tabs have no key — each is its own thing, so opening one never reuses another.
 */
export function tabKey(target: TabTarget): string | null {
  switch (target.kind) {
    case "model":
      return `model:${target.modelId}`;
    case "draft":
      return `draft:${target.draftId}`;
    case "dashboard":
      return `dashboard:${target.dashboardId}`;
    case "scratch":
      return null;
  }
}

/**
 * The entity a tab depends on, for resolution and pruning. Note that `model` and `draft` ids come
 * from different namespaces — a `Model` row versus a localStorage draft — so callers must match on
 * `type` as well as `id`.
 */
export type TabEntityRef =
  | { type: "model"; id: string }
  | { type: "draft"; id: string }
  | { type: "dashboard"; id: string };

export function targetEntityRef(target: TabTarget): TabEntityRef | null {
  switch (target.kind) {
    case "model":
      return { type: "model", id: target.modelId };
    case "draft":
      return { type: "draft", id: target.draftId };
    case "dashboard":
      return { type: "dashboard", id: target.dashboardId };
    case "scratch":
      return null;
  }
}

export function targetsEqual(a: TabTarget, b: TabTarget): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "model": {
      const other = b as ModelTabTarget;
      return (
        a.modelId === other.modelId &&
        a.dashboardId === other.dashboardId &&
        a.tileId === other.tileId
      );
    }
    case "draft":
      return a.draftId === (b as DraftTabTarget).draftId;
    case "dashboard":
      return a.dashboardId === (b as DashboardTabTarget).dashboardId;
    case "scratch":
      return true;
  }
}

export function isSameEntity(a: TabEntityRef, b: TabEntityRef) {
  return a.type === b.type && a.id === b.id;
}

export function targetsSameEntity(target: TabTarget, ref: TabEntityRef) {
  const targetRef = targetEntityRef(target);
  return targetRef != null && isSameEntity(targetRef, ref);
}
